/**
 * chatSegmentConfig.ts
 * Configuração pura de mapeamento de perfis → segmentos do chat.
 * Mantém a regra de negócio fora do JSX para o widget ficar limpo.
 */

export type ChatGroup = 'admin' | 'operadores' | 'familia' | 'clientes';

export type ChatSubFilter =
  | 'todos'
  | 'professores'
  | 'secretaria'
  | 'coordenadores'
  | 'responsaveis'
  | 'alunos'
  | 'escola'
  | 'municipio'
  | 'estado'
  | 'rede';

export interface SegmentTabConfig {
  key: string;
  label: string;
  color: string;
  badgeBg?: string;
}

export interface SegmentVisual {
  label: string;
  color: string;
  from: string;
  to: string;
  soft: string;
}

export const CHAT_GROUPS: SegmentTabConfig[] = [
  { key: 'admin', label: 'Admin', color: '#7c3aed', badgeBg: '#7c3aed' },
  { key: 'operadores', label: 'Professores / Equipes', color: '#2563eb', badgeBg: '#2563eb' },
  { key: 'familia', label: 'Responsáveis', color: '#059669', badgeBg: '#059669' },
  { key: 'clientes', label: 'Hierarquia / Gestores', color: '#d97706', badgeBg: '#d97706' },
];

export const CHAT_SUB_TABS: Record<ChatGroup, SegmentTabConfig[]> = {
  admin: [],
  operadores: [
    { key: 'todos', label: 'Todos', color: '#2563eb' },
    { key: 'professores', label: 'Professores', color: '#3b82f6' },
    { key: 'secretaria', label: 'Secretaria', color: '#1d4ed8' },
    { key: 'coordenadores', label: 'Coordenação', color: '#1e40af' },
  ],
  familia: [
    { key: 'todos', label: 'Todos', color: '#059669' },
    { key: 'responsaveis', label: 'Responsáveis', color: '#10b981' },
  ],
  clientes: [
    { key: 'todos', label: 'Todos', color: '#d97706' },
    { key: 'escola', label: 'Escola', color: '#f59e0b' },
    { key: 'municipio', label: 'Município', color: '#b45309' },
    { key: 'estado', label: 'Estado', color: '#92400e' },
    { key: 'rede', label: 'Rede', color: '#78350f' },
  ],
};

const ADMIN_PROFILES = ['ADMIN'];
const FAMILIA_PROFILES = ['RESPONSAVEL', 'RESPONSÁVEL'];
const CLIENTE_PROFILES = ['REDE', 'ESTADUAL', 'MUNICIPAL', 'ESCOLA'];
const OPERADOR_CARGOS = ['secretaria', 'secretário', 'secretária', 'coordenador', 'coordenadora', 'coordenação'];

export function normalizeProfile(perfil?: string | null): string {
  return (perfil || '').trim().toUpperCase();
}

export function normalizeCargo(cargo?: string | null): string {
  return (cargo || '').trim().toLowerCase();
}

export function isCargoOperacional(cargo?: string | null): boolean {
  const cargoLower = normalizeCargo(cargo);
  return OPERADOR_CARGOS.some((c) => cargoLower.includes(c));
}

export function classifyGroup(perfil: string, cargo?: string | null): ChatGroup {
  const perfilUpper = normalizeProfile(perfil);

  if (ADMIN_PROFILES.includes(perfilUpper)) return 'admin';
  if (FAMILIA_PROFILES.includes(perfilUpper)) return 'familia';
  if (perfilUpper === 'PROFESSOR') return 'operadores';

  if (perfilUpper === 'ESCOLA') {
    return isCargoOperacional(cargo) ? 'operadores' : 'clientes';
  }

  if (CLIENTE_PROFILES.includes(perfilUpper)) return 'clientes';
  return 'clientes';
}

export function matchSubFilter(
  perfil: string,
  cargo: string | null | undefined,
  group: ChatGroup,
  subFilter: ChatSubFilter
): boolean {
  if (subFilter === 'todos') return true;

  const perfilUpper = normalizeProfile(perfil);
  const cargoLower = normalizeCargo(cargo);

  switch (group) {
    case 'operadores':
      if (subFilter === 'professores') return perfilUpper === 'PROFESSOR';
      if (subFilter === 'secretaria') {
        return perfilUpper === 'ESCOLA' && ['secretaria', 'secretário', 'secretária'].some((c) => cargoLower.includes(c));
      }
      if (subFilter === 'coordenadores') {
        return perfilUpper === 'ESCOLA' && ['coordenador', 'coordenadora', 'coordenação'].some((c) => cargoLower.includes(c));
      }
      return false;

    case 'familia':
      if (subFilter === 'responsaveis') return perfilUpper === 'RESPONSAVEL' || perfilUpper === 'RESPONSÁVEL';
      if (subFilter === 'alunos') return perfilUpper === 'ALUNO';
      return false;

    case 'clientes':
      if (subFilter === 'escola') return perfilUpper === 'ESCOLA' && !isCargoOperacional(cargo);
      if (subFilter === 'municipio') return perfilUpper === 'MUNICIPAL';
      if (subFilter === 'estado') return perfilUpper === 'ESTADUAL';
      if (subFilter === 'rede') return perfilUpper === 'REDE';
      return false;

    default:
      return true;
  }
}

interface Countable {
  perfil: string;
  cargo?: string | null;
  unreadCount?: number;
}

export function countUnreadForGroup(items: Countable[], group: ChatGroup): number {
  return items
    .filter((item) => classifyGroup(item.perfil, item.cargo) === group)
    .reduce((sum, item) => sum + (item.unreadCount || 0), 0);
}

export function countUnreadForSubFilter(
  items: Countable[],
  group: ChatGroup,
  subFilter: ChatSubFilter
): number {
  return items
    .filter((item) => classifyGroup(item.perfil, item.cargo) === group && matchSubFilter(item.perfil, item.cargo, group, subFilter))
    .reduce((sum, item) => sum + (item.unreadCount || 0), 0);
}

export function filterBySegment<T extends { perfil: string; cargo?: string | null }>(
  items: T[],
  group: ChatGroup,
  subFilter: ChatSubFilter
): T[] {
  return items.filter((item) => classifyGroup(item.perfil, item.cargo) === group && matchSubFilter(item.perfil, item.cargo, group, subFilter));
}

export function getSegmentVisual(perfil?: string | null, cargo?: string | null): SegmentVisual {
  const group = classifyGroup(perfil || '', cargo);
  const perfilUpper = normalizeProfile(perfil);
  const cargoLower = normalizeCargo(cargo);

  if (group === 'admin') {
    return { label: 'ADMIN', color: '#7c3aed', from: '#8b5cf6', to: '#4c1d95', soft: 'rgba(124,58,237,.12)' };
  }

  if (group === 'familia') {
    if (perfilUpper === 'ALUNO') {
      return { label: 'ALUNO', color: '#047857', from: '#34d399', to: '#047857', soft: 'rgba(5,150,105,.12)' };
    }
    return { label: 'FAMÍLIA', color: '#059669', from: '#10b981', to: '#065f46', soft: 'rgba(5,150,105,.12)' };
  }

  if (group === 'operadores') {
    if (perfilUpper === 'PROFESSOR') {
      return { label: 'PROFESSOR', color: '#2563eb', from: '#60a5fa', to: '#1d4ed8', soft: 'rgba(37,99,235,.12)' };
    }
    if (cargoLower.includes('secret')) {
      return { label: 'SECRETARIA', color: '#1d4ed8', from: '#38bdf8', to: '#1d4ed8', soft: 'rgba(29,78,216,.12)' };
    }
    return { label: 'COORDENAÇÃO', color: '#1e40af', from: '#818cf8', to: '#1e3a8a', soft: 'rgba(30,64,175,.12)' };
  }

  if (perfilUpper === 'MUNICIPAL') {
    return { label: 'MUNICIPAL', color: '#b45309', from: '#f59e0b', to: '#b45309', soft: 'rgba(180,83,9,.12)' };
  }

  if (perfilUpper === 'ESTADUAL') {
    return { label: 'ESTADUAL', color: '#92400e', from: '#fb923c', to: '#92400e', soft: 'rgba(146,64,14,.12)' };
  }

  if (perfilUpper === 'REDE') {
    return { label: 'REDE', color: '#78350f', from: '#f97316', to: '#78350f', soft: 'rgba(120,53,15,.12)' };
  }

  return { label: 'ESCOLA', color: '#d97706', from: '#fbbf24', to: '#d97706', soft: 'rgba(217,119,6,.12)' };
}

export function formatRole(perfil?: string | null, cargo?: string | null, municipioNome?: string | null): string {
  const visual = getSegmentVisual(perfil, cargo);
  const rawCargo = (cargo || '').trim();
  
  let label = visual.label;
  if (perfil?.toUpperCase() === 'MUNICIPAL' && municipioNome) {
    label = `${visual.label} - ${municipioNome}`;
  }
  
  return rawCargo ? `${label} · ${rawCargo}` : label;
}

export function getSingleRoleName(perfil?: string | null, cargo?: string | null): string {
  const p = normalizeProfile(perfil);
  const c = normalizeCargo(cargo);

  // Se tem cargo específico e formatado
  if (c.includes('vice') && (c.includes('diret') || c.includes('gest'))) {
    return 'Vice-Diretor(a)';
  }
  if (c.includes('diret') || c.includes('gestor')) {
    return 'Diretor(a)';
  }
  if (c.includes('secret')) {
    return 'Secretaria';
  }
  if (c.includes('coord')) {
    return 'Coordenação';
  }
  if (c.includes('superv')) {
    return 'Supervisão';
  }
  if (c.includes('orient')) {
    return 'Orientação';
  }
  if (c.includes('apoio') || c.includes('auxiliar')) {
    return 'Apoio Escolar';
  }
  if (c.includes('prof') || p === 'PROFESSOR') {
    return 'Professor(a)';
  }
  if (p === 'RESPONSAVEL') {
    return 'Responsável';
  }
  if (p === 'ALUNO') {
    return 'Aluno';
  }
  if (p === 'ADMIN') {
    return 'Admin';
  }
  if (p === 'MUNICIPAL') {
    return 'Municipal';
  }
  if (p === 'ESTADUAL') {
    return 'Estadual';
  }
  if (p === 'REDE') {
    return 'Rede';
  }
  if (p === 'ESCOLA') {
    return 'Equipe Escolar';
  }

  const raw = (cargo || '').trim();
  if (raw && raw.toUpperCase() !== p) {
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }

  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
}
